<template>
  <div style="min-height: 80vh">
    <v-container v-if="entityData" class="entity-page">
      <agent-notice v-if="showAgentNotice" :api-url="recordApiUrl" class="mt-2 mb-3" />
      <entity-header
        :entity-data="entityData"
        :entity-type="myEntityType"
        class="mb-4 mt-2"
      >
        <template v-if="isAuthorOwner" #after-title>
          <AuthorDisplayNameEditor
            :current-display-name="entityData.display_name"
            :is-owner="isAuthorOwner"
            :pending="displayNamePending"
            @update-name="handleDisplayNameUpdate"
          />
        </template>
      </entity-header>

      <v-row v-if="myEntityType === 'works'">
        <v-col :cols="12" :md="showEntityPageStats ? 7 : 12">
          <v-card variant="outlined" rounded class="bg-white">
            <v-tabs v-model="activeTab" bg-color="transparent">
              <v-tab value="details">Details</v-tab>
              <v-tab value="locations">Locations</v-tab>
            </v-tabs>

            <v-divider />

            <v-window v-model="activeTab">
              <v-window-item value="details">
                <div class="py-6">
                  <entity-new
                    :data="entityData"
                    :type="myEntityType"
                  />
                </div>
              </v-window-item>

              <v-window-item value="locations">
                <div class="pa-6">
                  <v-alert v-if="allLocations.length === 0" type="info" variant="tonal">
                    No locations available for this work.
                  </v-alert>
                  <v-row v-else>
                    <v-col
                      v-for="(location, index) in allLocations"
                      :key="index"
                      cols="12"
                      sm="6"
                      xl="4"
                      class="d-flex"
                    >
                      <v-card variant="outlined" :elevation="0" class="d-flex flex-column" style="width: 100%;">
                        <v-card-text class="py-4">
                          <!-- Use EntityNew component to show all location fields -->
                          <entity-new
                            :data="location"
                            type="locations"
                          />
                        </v-card-text>
                        
                        <!-- Link to the location's own page (oxjob #852) + chips.
                             Embedded rows carry bare namespaced ids (doi:…,
                             pmid:…) — route by path, slashes preserved, same as
                             filters.js entityPageTarget. -->
                        <v-card-actions class="d-flex">
                          <v-btn
                            v-if="location.id"
                            variant="text"
                            size="small"
                            class="text-none"
                            :to="{ path: `/locations/${location.id}` }"
                          >
                            Location page
                            <v-icon size="14" class="ml-1">mdi-arrow-right</v-icon>
                          </v-btn>
                          <v-spacer />
                          <v-chip
                            v-if="location.isPrimary"
                            size="x-small"
                            variant="outlined"
                            class="mr-1"
                          >
                            primary
                          </v-chip>
                          <v-chip
                            v-if="location.isBestOa"
                            size="x-small"
                            variant="outlined"
                          >
                            best oa
                          </v-chip>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-window-item>
            </v-window>
          </v-card>
        </v-col>

        <!-- Works sidebar: just the metrics block for now (FWCI, cites, cited
             by, …). Same shape as the non-works sidebar below; group-bys are
             omitted because the obvious "works grouped by X, filtered by this
             work's id" returns exactly one row. Citing/cited group-bys can
             land here later as a follow-up. -->
        <v-col v-if="showEntityPageStats" cols="12" md="5">
          <v-card flat class="rounded-o px-2 pt-4 pb-3">
            <entity-metrics
              :data="entityData"
              :type="myEntityType"
              class="entity-metrics-block mb-3 pb-3"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- Generic entity page (non-works) -->
      <v-row v-else>
        <v-col v-if="showEntityPageStats" cols="12" md="7">
          <!-- Repository sources get a Details/Harvest tab strip (oxjob #836);
               every other entity keeps the flat details card. -->
          <v-card v-if="isRepositorySource" variant="outlined" class="rounded-o bg-white">
            <v-tabs v-model="activeTab" bg-color="transparent">
              <v-tab value="details">Details</v-tab>
              <v-tab value="harvest">Harvest (beta)</v-tab>
            </v-tabs>
            <v-divider />
            <v-window v-model="activeTab">
              <v-window-item value="details">
                <div class="py-6">
                  <entity-new
                    :data="entityData"
                    :type="myEntityType"
                  />
                </div>
              </v-window-item>
              <v-window-item value="harvest">
                <source-harvest-tab :entity-data="entityData" />
              </v-window-item>
            </v-window>
          </v-card>
          <v-card v-else variant="outlined" class="rounded-o py-6 bg-white">
            <entity-new
              :data="entityData"
              :type="myEntityType"
            />
          </v-card>

          <v-card v-if="!isComponentEntity" variant="outlined" class="rounded-o mt-3 bg-white">
            <selection-toolbar :selectable="worksSelectable" :disable-master="isAuthorOwner && worksFilterActive">
              <template #trailing>
                <v-spacer/>
                <v-chip
                  v-if="isAuthorOwner && pendingCount"
                  :variant="worksFilterActive ? 'flat' : 'outlined'"
                  :color="worksFilterActive ? 'primary' : undefined"
                  size="small"
                  class="mr-2"
                  :append-icon="worksFilterActive ? 'mdi-close-circle' : undefined"
                  @click="worksFilterActive = !worksFilterActive"
                >
                  {{ pendingCount }} pending
                </v-chip>
                <template v-if="isAuthorOwner">
                  <v-tooltip location="bottom" text="Remove works">
                    <template v-slot:activator="{ props: rmProps }">
                      <v-btn
                        v-bind="rmProps"
                        variant="text"
                        icon
                        class="oa-cur-iconbtn"
                        :disabled="!worksCuration.canRemove.value"
                        aria-label="Remove works"
                        @click="worksCuration.removeSelected()"
                      >
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                  <v-menu v-model="addMenuOpen" location="bottom end">
                    <template v-slot:activator="{ props: menuProps }">
                      <v-tooltip location="bottom" text="Add works" :disabled="addMenuOpen">
                        <template v-slot:activator="{ props: addTip }">
                          <v-btn
                            v-bind="mergeProps(menuProps, addTip)"
                            variant="text"
                            icon
                            aria-label="Add works"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </template>
                    <v-list density="compact">
                      <v-list-item title="Add from search" @click="worksCuration.openSearch()" />
                      <v-list-item title="Add from CV" @click="worksCuration.openCv()" />
                    </v-list>
                  </v-menu>
                </template>
                <v-menu location="bottom end">
                  <template v-slot:activator="{ props: kebabProps }">
                    <v-btn
                      v-bind="kebabProps"
                      variant="plain"
                      icon
                      aria-label="More"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <!-- Sources get explicit primary/any-source works links
                         (oxjob #836 Decision 3) instead of the ambiguous
                         "View as search". -->
                    <template v-if="myEntityType === 'sources'">
                      <v-list-item
                        title="Works with this as primary source"
                        @click="viewWorksBy('primary_location.source.id')"
                      />
                      <v-list-item
                        title="Works with this as any source"
                        @click="viewWorksBy('locations.source.id')"
                      />
                      <v-list-item
                        title="About primary vs non-primary sources"
                        href="https://help.openalex.org/data/works/"
                        target="_blank"
                      />
                    </template>
                    <v-list-item v-else title="View as search" @click="viewMyWorks" />
                  </v-list>
                </v-menu>
              </template>
            </selection-toolbar>

            <div v-if="isAuthorOwner">
              <template v-if="worksFilterActive">
                <div
                  v-for="(item, idx) in worksCuration.pendingAdditions.value"
                  :key="item.work.id"
                  class="oa-cur-row"
                >
                  <div class="oa-cur-body">
                    <serp-results-list-item
                      :result="item.work"
                      pending-state="add"
                      disable-select
                      :selectable="worksSelectable"
                    />
                  </div>
                  <div class="oa-cur-badge">
                    <v-btn variant="text" size="x-small" @click="worksCuration.undoAddition(idx)">
                      Cancel
                    </v-btn>
                  </div>
                </div>
                <div
                  v-for="item in worksCuration.pendingRemovalWorks.value"
                  :key="item.work.id"
                  class="oa-cur-row"
                >
                  <div class="oa-cur-body">
                    <serp-results-list-item
                      :result="item.work"
                      pending-state="remove"
                      disable-select
                      :selectable="worksSelectable"
                    />
                  </div>
                  <div class="oa-cur-badge">
                    <v-btn variant="text" size="x-small" @click="worksCuration.undoRemoval(item.work.id)">
                      Cancel
                    </v-btn>
                  </div>
                </div>
              </template>
              <div
                v-for="result in visibleResults"
                :key="result.id"
                class="oa-cur-row"
              >
                <div class="oa-cur-body">
                  <serp-results-list-item
                    :result="result"
                    :pending-state="worksCuration.isPendingRemoval(result.id) ? 'remove' : null"
                    :disable-select="worksFilterActive"
                    :selectable="worksSelectable"
                  />
                </div>
                <div v-if="worksCuration.isPendingRemoval(result.id)" class="oa-cur-badge">
                  <v-btn variant="text" size="x-small" @click="worksCuration.undoRemoval(result.id)">
                    Cancel
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else>
              <serp-results-list-item
                  v-for="result in worksResultObject.results"
                  :key="result.id"
                  :result="result"
                  :selectable="worksSelectable"
              />
            </div>

            <div
              v-if="hasMoreWorks || worksResultObject.results?.length"
              class="pa-3 text-center"
            >
              <v-btn
                variant="text"
                rounded
                size="small"
                color="primary"
                prepend-icon="mdi-chevron-down"
                :disabled="!hasMoreWorks"
                :loading="worksLoadingMore"
                @click="showMoreWorks"
              >
                Show more works
              </v-btn>
              <v-btn
                variant="text"
                rounded
                size="small"
                color="primary"
                prepend-icon="mdi-open-in-new"
                @click="viewMyWorks"
              >
                View as search
              </v-btn>
            </div>

            <template v-if="isAuthorOwner">
              <AddWorksModal
                v-model="worksCuration.searchOpen.value"
                mode="search"
                :author-name="entityData.display_name"
                :author-id="entityData.id"
                @add-work="worksCuration.onAddWork"
              />
              <AddWorksModal
                v-model="worksCuration.cvOpen.value"
                mode="cv"
                :author-name="entityData.display_name"
                :author-id="entityData.id"
                @add-work="worksCuration.onAddWork"
              />
              <ObservedNamesEditor
                v-model="observedNamesDialogOpen"
                :name-rows="observedNamesCuration.nameRows.value"
                :loading="observedNamesCuration.loading.value"
                :submitting="observedNamesCuration.submitting.value"
                :progress="observedNamesCuration.progress.value"
                :too-large="observedNamesCuration.tooLarge.value"
                @submit="onRemoveObservedNames"
              />
            </template>
          </v-card>
        </v-col>

        <v-col v-else cols="12">
          <v-card v-if="isRepositorySource" flat class="rounded-o">
            <v-tabs v-model="activeTab" bg-color="transparent">
              <v-tab value="details">Details</v-tab>
              <v-tab value="harvest">Harvest (beta)</v-tab>
            </v-tabs>
            <v-divider />
            <v-window v-model="activeTab">
              <v-window-item value="details">
                <div class="py-6">
                  <entity-new
                    :data="entityData"
                    :type="myEntityType"
                  />
                </div>
              </v-window-item>
              <v-window-item value="harvest">
                <source-harvest-tab :entity-data="entityData" />
              </v-window-item>
            </v-window>
          </v-card>
          <v-card v-else flat class="rounded-o py-6">
            <entity-new
              :data="entityData"
              :type="myEntityType"
            />
          </v-card>
        </v-col>

        <!-- Component entities (oxjob #852) have no works metrics or group-bys —
             without the guard this renders as an empty white pill. -->
        <v-col v-if="showEntityPageStats && !isComponentEntity" cols="12" md="5">
          <v-card flat class="rounded-o px-2 pt-4 pb-3">
            <entity-metrics
              :data="entityData"
              :type="myEntityType"
              class="entity-metrics-block mb-3 pb-3"
            />
            <template v-if="!isComponentEntity">
              <group-by
                v-for="groupByKey in groupByKeys"
                :key="groupByKey"
                :filter-key="groupByKey"
                :filter-by="[myWorksFilter]"
                entity-type="works"
                :is-entity-page="true"
                class="mb-3"
              />
            </template>
          </v-card>
        </v-col>
      </v-row>

    </v-container>

    <v-container v-else-if="entityLoadFailed">
      <div class="text-h1">
        Page not found
      </div>
      <div class="mt-12">
        Sorry, there's no {{ myEntityType }} page for "{{ route.params.entityId }}".
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, mergeProps, provide } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useAsyncData, useRequestEvent, useState } from '#app';  // #1689 Nuxt trial: record data fetched on the server, reused on hydration

import { api } from '@/api';
import { url } from '@/url';
import { getEntityConfig } from '@/entityConfigs';
import { tagRecordData, recordDataFor } from '@/ssr/recordData';
import { createSimpleFilter, filtersAsUrlStr } from '@/filterConfigs';
import { getFacetConfig } from '@/facetConfigUtils';
import { recordHead, apiUrl } from '@/seo/recordHead.mjs';

import EntityNew from '@/components/Entity/EntityNew.vue';
import AgentNotice from '@/components/Entity/AgentNotice.vue';
import EntityHeader from '@/components/Entity/EntityHeader.vue';
import EntityMetrics from '@/components/Entity/EntityMetrics.vue';
import SerpResultsListItem from '@/components/SerpResultsListItem.vue';
import SelectionToolbar from '@/components/SelectionToolbar.vue';
import GroupBy from '@/components/GroupBy/GroupBy.vue';
import SourceHarvestTab from '@/components/Entity/SourceHarvestTab.vue';
import { useSelectionContext } from '@/composables/useSelectionContext';

// Author curation components (feature-flagged display-name editors only)
import AuthorDisplayNameEditor from '@/components/AuthorCuration/AuthorDisplayNameEditor.vue';
import ObservedNamesEditor from '@/components/AuthorCuration/ObservedNamesEditor.vue';
import AddWorksModal from '@/components/AuthorCuration/AddWorksModal.vue';
import { useAuthorWorksCuration } from '@/composables/useAuthorWorksCuration';
import { useObservedNamesCuration } from '@/composables/useObservedNamesCuration';

defineOptions({ name: 'EntityPage' });

const store = useStore();
const route = useRoute();
const router = useRouter();

const myEntityType = ref(route.params.entityType);
const worksPage = ref(1);
const worksLoadingMore = ref(false);
// Non-default tabs: 'locations' (works pages), 'harvest' (repository sources, #836).
const ENTITY_TABS = ['locations', 'harvest'];
const activeTab = ref(ENTITY_TABS.includes(route.query.tab) ? route.query.tab : 'details');

const WORKS_PER_PAGE = 25;

const hasMoreWorks = computed(() => {
  const loaded = worksResultObject.value.results?.length || 0;
  const total = worksResultObject.value.meta?.count || 0;
  return loaded > 0 && loaded < total;
});

const myEntityConfig = computed(() => myEntityType.value ? getEntityConfig(myEntityType.value) : null);

// Component entities (locations, oxjob #852) have no works-rollup UI: their
// filterKey isn't a valid works filter (works?filter=locations.id:… 400s),
// and the "works from this entity" set is exactly one work — already linked
// from the details rows. Hide the works list + group-by rail for them.
const isComponentEntity = computed(() => myEntityConfig.value?.entityClass === 'component');

const myWorksFilter = computed(() => {
  if (!myEntityConfig.value) return null;
  // For repository sources, use locations.source.id to show all works
  // where this repo appears in any location (not just primary)
  const isRepository = myEntityType.value === 'sources' && entityData.value?.type === 'repository';
  const filterKey = isRepository ? 'locations.source.id' : myEntityConfig.value.filterKey;
  return createSimpleFilter('works', filterKey, route.params.entityId);
});

const apiPath = computed(() => `${route.params.entityType}/${route.params.entityId}`);

// #1689 Nuxt trial: the record comes from useAsyncData (runs on the server for
// SSR'd routes, serialized into the Nuxt payload, reused by the browser on
// hydration; refetches when the key changes on client-side navigation).
const massageEntity = (data) => {
  // For locations, massage the data to create entity objects for better rendering
  if (route.params.entityType === 'locations' && data) {
    if (!data.id) {
      data.id = route.params.entityId;
    }
    if (data.source_id && data.source_name) {
      data.source = {
        id: data.source_id,
        display_name: data.source_name
      };
    }
    if (data.work_id) {
      const shortId = data.work_id.replace('https://openalex.org/', '');
      data.work = {
        id: data.work_id,
        display_name: shortId // Show as "works/W12345" format
      };
    }
  }
  return data;
};

// #1689: on the server, the record and the first works page come from the record
// middleware (server/middleware/02.records.ts: the server's key, a timeout, 404/301
// handled before render). This component never calls the API on the server.
const ssrRecord = import.meta.server ? useRequestEvent()?.context?.oxRecord : null;
const ssrRecordFor = (path) => {
  if (!ssrRecord || ssrRecord.apiPath !== path) {
    // Not reachable: the middleware turns SSR off for any record URL it didn't load.
    throw new Error(`record SSR: no prefetched record for ${path}`);
  }
  if (ssrRecord.notFound) {
    throw Object.assign(new Error('Record not found'), { statusCode: 404 });
  }
  return ssrRecord;
};

const {
  data: entityAsync,
  error: entityAsyncError,
  status: entityAsyncStatus,
} = useAsyncData(
  () => `entity:${apiPath.value}`,
  async () => {
    const path = apiPath.value;
    return tagRecordData(path, massageEntity(import.meta.server ? ssrRecordFor(path).data : await api.get(path)));
  },
);
// Only the record fetched for THIS route: on a client-side navigation Nuxt seeds
// the new key with the previous record while it loads (src/ssr/recordData.js).
const entityData = computed(() => recordDataFor(entityAsync.value, apiPath.value));
// #1689: the "Notice to agents" (verified AI assistants, scripts, HTTP libraries).
// The record middleware decides per request (server/lib/agentNotice.mjs); Nuxt state
// carries the answer to the browser so hydration renders the same DOM. A browser
// that started on any other page never has it.
const agentNotice = useState('ox-agent-notice', () => !!ssrRecord?.agentNotice);
const recordApiUrl = computed(() => apiUrl(entityData.value?.id));
const showAgentNotice = computed(() => agentNotice.value && !!recordApiUrl.value);
// Bad/unknown id: show the failure state instead of a blank page (oxjob #671).
const entityLoadFailed = computed(() => !!entityAsyncError.value);
// #1689: the not-found page (status 404 from the server) says so in its title.
useHead(() => (entityLoadFailed.value ? { title: 'Page not found' } : {}));
watch(entityAsyncStatus, (st) => {
  if (typeof window !== 'undefined') store.state.isLoading = st === 'pending';
}, { immediate: true });


// Repository sources get the Harvest tab (oxjob #836). Null-tolerant:
// entityData is null until the fetch resolves.
const isRepositorySource = computed(() =>
  myEntityType.value === 'sources' && entityData.value?.type === 'repository'
);

const groupByKeys = computed(() => {
  if (myEntityType.value === 'awards') {
    return ['publication_year', 'primary_topic.id'];
  }
  return ['publication_year', 'primary_topic.id', 'type'];
});

const isAward = computed(() => myEntityType.value === 'awards');

const showEntityPageStats = computed(() => store.state.showEntityPageStats);

// Author curation computed properties
const isAuthor = computed(() => myEntityType.value === 'authors');
const userAuthorId = computed(() => store.getters['user/userAuthorId']);
const isAuthorOwner = computed(() => {
  if (!isAuthor.value || !userAuthorId.value || !entityData.value?.id) return false;
  const normalize = (id) => (String(id || '').match(/A\d+/i) || [''])[0].toUpperCase();
  const a = normalize(userAuthorId.value);
  return !!a && a === normalize(entityData.value.id);
});
// Selection on author works powers the owner-curation flow. oxjob #187.
// (Previously also gated by the now-decommissioned selectable_serp flag.)
const worksSelectable = computed(() => isAuthorOwner.value);

// Owner-only add/remove-works curation, layered on the existing works list
// and the shared selection store. oxjob #187.
// #1689: the first page of the works list is fetched with the record (server
// side too); "show more" pages are appended in the browser.
// On the server the list is trimmed to what a row shows (src/ssr/recordRoutes.js);
// if it was slow, the server sends `{deferred: true}` and the browser fetches it
// after hydration (onMounted below).
const { data: worksFirstPageAsync, refresh: refreshWorksFirstPage } = useAsyncData(
  () => `entity-works:${apiPath.value}`,
  async () => {
    const path = apiPath.value;
    if (import.meta.server) {
      if (!ssrRecord?.works || ssrRecord.apiPath !== path || !showEntityPageStats.value) return tagRecordData(path, {});
      return tagRecordData(path, (await ssrRecord.works) ?? { deferred: true });
    }
    return tagRecordData(path, (await fetchWorksPage(1)) ?? {});
  },
);
// Same guard as entityData: never show the previous record's works list.
const worksFirstPage = computed(() => recordDataFor(worksFirstPageAsync.value, apiPath.value) ?? {});
const worksMore = ref(null);
const worksResultObject = computed(() => worksMore.value ?? worksFirstPage.value ?? {});
const addMenuOpen = ref(false);
const worksCuration = useAuthorWorksCuration({
  authorId: computed(() => (isAuthorOwner.value ? entityData.value?.id || '' : '')),
  authorName: computed(() => entityData.value?.display_name || ''),
  works: computed(() => worksResultObject.value.results || []),
});

// "n pending" quasi-filter: when active the list shows ONLY works with a
// pending add/remove; when inactive those works are hidden entirely.
const worksFilterActive = ref(false);
const pendingCount = computed(() => worksCuration.pendingCount.value);
watch(pendingCount, (n) => {
  if (!n) worksFilterActive.value = false;
});
const visibleResults = computed(() => {
  const results = worksResultObject.value.results || [];
  if (!isAuthorOwner.value) return results;
  if (worksFilterActive.value) {
    return results.filter((r) => worksCuration.isPendingRemoval(r.id));
  }
  return results.filter((r) => !worksCuration.isPendingRemoval(r.id));
});

// Name-level "remove observed names" (oxjob #342). Layers a name->works
// mapping + curation-derived name visibility over the SAME per-work removal
// machinery: removals funnel through `submitAuthorCurations` and `onSubmitted`
// re-runs the per-work reconcile so the "{n} pending" chip + per-work
// strikethrough pick them up. Observed names = display_name_alternatives minus
// the primary display_name (the owner can't disown their own primary name).
const observedNames = computed(() => {
  const d = entityData.value;
  if (!d) return [];
  return (d.display_name_alternatives || []).filter(
    (n) => n && n !== d.display_name
  );
});
const observedNamesCuration = useObservedNamesCuration({
  authorId: computed(() => (isAuthorOwner.value ? entityData.value?.id || '' : '')),
  observedNames,
  onSubmitted: () => worksCuration.reconcile(),
  worksCount: computed(() => entityData.value?.works_count || 0),
});
const observedNamesDialogOpen = ref(false);
async function openObservedNamesDialog() {
  observedNamesDialogOpen.value = true;
  await observedNamesCuration.refreshCurations();
  if (!observedNamesCuration.mapped.value) {
    await observedNamesCuration.buildMapping();
  }
}
async function onRemoveObservedNames(names) {
  await observedNamesCuration.removeNames(names);
}
// Reload persistence (EXPLORE Q2): if the owner already has remove-curations
// in flight, build the mapping once in the background so struck/hidden name
// state is reconstructed from curations — the server doc lags removals >24h.
watch(
  () => [isAuthorOwner.value, entityData.value?.id],
  async ([owner, id]) => {
    if (!owner || !id) return;
    await observedNamesCuration.refreshCurations();
    // Only pay for the full works pagination when removals are actually in
    // flight — otherwise names render normally with no mapping cost.
    if (observedNamesCuration.hasRemovals.value) {
      await observedNamesCuration.buildMapping();
    }
  },
  { immediate: true }
);
// Expose owner-curation context to the deeply-nested observed-names row
// (EntityNew -> EntityDatumRow) without prop-drilling through the shared
// generic components. EntityDatumRow injects this for filterKey
// 'display_name_alternatives'.
provide('observedNamesOwnerCuration', {
  enabled: computed(() => isAuthorOwner.value),
  isNamePending: observedNamesCuration.isNamePending,
  isNameRemoved: observedNamesCuration.isNameRemoved,
  openDialog: openObservedNamesDialog,
});
const allLocations = computed(() => {
  if (!entityData.value || myEntityType.value !== 'works') return [];
  
  const work = entityData.value;
  
  // Helper function to create a unique key for location comparison
  const getLocationKey = (loc) => {
    if (!loc) return null;
    // Use landing_page_url as unique identifier, or fallback to stringified object
    return loc.landing_page_url || JSON.stringify(loc);
  };
  
  // Just use the locations array (which already contains primary and best OA)
  // and mark which ones are special
  const locations = [];
  const primaryKey = getLocationKey(work.primary_location);
  const bestOaKey = getLocationKey(work.best_oa_location);
  
  if (work.locations && Array.isArray(work.locations)) {
    work.locations.forEach(loc => {
      const locKey = getLocationKey(loc);
      locations.push({
        ...loc,
        isPrimary: locKey === primaryKey,
        isBestOa: locKey === bestOaKey
      });
    });
  }
  
  return locations;
});

async function fetchWorksPage(page) {
  if (myEntityType.value === 'works') return null;
  if (!myEntityConfig.value) return null;
  if (isComponentEntity.value) return null; // no works rollup (oxjob #852)
  if (!showEntityPageStats.value) return null;

  const filterString = filtersAsUrlStr([myWorksFilter.value]);
  // For awards, sort by publication year (most recent funded works first)
  // For other entities, sort by citation count (most cited works first)
  const sortOrder = isAward.value ? 'publication_year:desc' : 'cited_by_count:desc';
  const apiUrl = api.makeUrl('works', {
    filter: filterString,
    sort: sortOrder,
    'per-page': WORKS_PER_PAGE,
    page,
  }, true);

  return api.getResultsList(apiUrl);
}

const getWorks = async () => {
  worksMore.value = null;
  worksPage.value = 1;
};

const showMoreWorks = async () => {
  if (worksLoadingMore.value || !hasMoreWorks.value) return;
  worksLoadingMore.value = true;
  try {
    const nextPage = worksPage.value + 1;
    const resp = await fetchWorksPage(nextPage);
    if (resp?.results?.length) {
      worksMore.value = {
        ...resp,
        results: [...(worksResultObject.value.results || []), ...resp.results],
      };
      worksPage.value = nextPage;
    }
  } finally {
    worksLoadingMore.value = false;
  }
};

const viewMyWorks = () => {
  return url.pushNewFilters([myWorksFilter.value], 'works');
};

// Kebab items on source pages: explicit primary-source / any-source works
// searches (oxjob #836 Decision 3).
const viewWorksBy = (filterKey) => {
  const filter = createSimpleFilter('works', filterKey, route.params.entityId);
  return url.pushNewFilters([filter], 'works');
};

// Author curation handlers (oxjob #187, partial-success aware since #291)
const displayNamePending = ref(false);
const handleDisplayNameUpdate = async (newName) => {
  try {
    const {rows, skipped, errors} = await store.dispatch('user/submitAuthorCurations', [{
      entity: 'authors',
      entity_id: entityData.value.id,
      property: 'display_name',
      action: 'replace',
      value: newName,
    }]);
    // Single-element batch: at most one of rows/skipped/errors has an item.
    if (errors.length) {
      store.commit('snackbar', errors[0].error || 'Edit failed');
      return;
    }
    if (skipped.length) {
      // No-op (was a 409 pre-#291): the display name already equals newName.
      store.commit('snackbar', skipped[0].reason);
      return;
    }
    if (rows.length) {
      displayNamePending.value = true;
      store.commit('snackbar', 'Edit submitted');
    }
  } catch (e) {
    store.commit('snackbar', e.message);
  }
};

// #1689: title, description, canonical, robots, link previews and JSON-LD come from
// one function, the same on the server render and in the browser (src/seo/recordHead.mjs).
const shownAbstract = computed(() => {
  if (myEntityType.value !== 'works' || !entityData.value) return '';
  return (getFacetConfig('works', 'abstract')?.extractFn(entityData.value) || '').trim();
});
useHead(() => recordHead(myEntityType.value, entityData.value, { abstract: shownAbstract.value }));

onMounted(() => {
  console.log('EntityPage mounted', route.params.entityType, route.params.entityId);
  store.state.entityType = route.params.entityType;
  if (worksFirstPage.value?.deferred) refreshWorksFirstPage();
});

watch(apiPath, async () => {
  myEntityType.value = route.params.entityType;
  // The 'harvest' tab only exists on repository sources; landing anywhere else
  // with ?tab=harvest (stale link, cross-entity navigation) would render an
  // empty window. Fall back to details.
  // (#1689: the record may still be loading here; the watcher below re-checks.)
  if (activeTab.value === 'harvest' && entityData.value && !isRepositorySource.value) {
    activeTab.value = 'details';
  }
  await getWorks();
}, { immediate: true });
watch(entityData, (d) => {
  if (d && activeTab.value === 'harvest' && !isRepositorySource.value) activeTab.value = 'details';
});

// Sync activeTab to URL query parameter
watch(activeTab, (newTab) => {
  if (ENTITY_TABS.includes(newTab)) {
    router.replace({ query: { ...route.query, tab: newTab } });
  } else {
    // Remove tab query parameter for details (default)
    const query = { ...route.query };
    delete query.tab;
    router.replace({ query });
  }
});

// Update activeTab when URL changes
watch(() => route.query.tab, (newTab) => {
  activeTab.value = ENTITY_TABS.includes(newTab) ? newTab : 'details';
});

useSelectionContext(() => worksResultObject.value);
</script>


<style lang="scss">
.entity-page-section-title .v-toolbar__content {
  margin-inline-start: 0px !important;
  padding: 4px 12px !important;
}
.entity-page-section-title .v-toolbar-title {
  margin-inline-start: 0px !important;
}
.entity-page  .v-list .v-list-item--active {
  color: #1976d2; // primary
}
.entity-page .text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.entity-page .entity-metrics-block {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.entity-page .oa-cur-row {
  display: flex;
  align-items: flex-start;
}
.entity-page .oa-cur-body {
  flex: 1 1 auto;
  min-width: 0;
}
.entity-page .oa-cur-badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 16px 12px 0 4px;
  white-space: nowrap;
}
.entity-page .oa-cur-iconbtn.v-btn--disabled {
  background: transparent !important;
}
.entity-page .oa-cur-iconbtn.v-btn--disabled .v-btn__overlay,
.entity-page .oa-cur-iconbtn.v-btn--disabled .v-btn__underlay {
  opacity: 0 !important;
}
</style>